import { useState, useEffect, useMemo } from "react";
import api, { fileUrl } from "@/lib/api";
import { fallbackFor, variantBadge, variantLabel, categoryBadge, categoryLabel, isAdult, money } from "@/lib/constants";
import { useAgeGate } from "@/context/AgeGateContext";
import MagnifierImage from "@/components/MagnifierImage";
import { toast } from "sonner";
import { X, Pencil, Trash2, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, ShieldAlert } from "lucide-react";

function Row({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-stroke/60">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-gray">{label}</span>
      <span className="font-mono text-sm text-paper text-right">{value}</span>
    </div>
  );
}

export default function PosterDetail({ poster, index = 0, onClose, onEdit, onDeleted }) {
  const [imgIdx, setImgIdx] = useState(0);
  const { verified, requestVerify } = useAgeGate();

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const venue = useMemo(
    () => [poster?.venue_name, poster?.venue_city].filter(Boolean).join(", "),
    [poster?.venue_name, poster?.venue_city]
  );

  if (!poster) return null;

  const imgs =
    poster.image_paths && poster.image_paths.length
      ? poster.image_paths.map(fileUrl)
      : [fallbackFor(poster.category, index)];

  const gain =
    poster.purchase_price != null && poster.market_value != null
      ? poster.market_value - poster.purchase_price
      : null;

  if (isAdult(poster) && !verified) {
    return (
      <div onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center bg-ink/90 backdrop-blur-sm p-4">
        <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-surface border border-red-500/50 p-8 text-center" data-testid="poster-detail-locked">
          <ShieldAlert className="text-red-400 mx-auto mb-4" size={40} />
          <h2 className="font-display text-4xl text-paper">18+ Content</h2>
          <p className="font-mono text-xs text-muted-gray mt-3">Verify your age to view this poster.</p>
          <div className="mt-6 flex gap-3">
            <button onClick={onClose} className="flex-1 py-3.5 font-mono text-xs uppercase tracking-wider border border-stroke text-muted-gray hover:text-paper">Close</button>
            <button onClick={requestVerify} data-testid="poster-detail-verify" className="flex-1 py-3.5 font-mono text-xs uppercase tracking-wider bg-red-500 text-white font-bold hover:bg-red-600">Verify Age</button>
          </div>
        </div>
      </div>
    );
  }


  const del = async () => {
    if (!window.confirm("Delete this poster from your vault?")) return;
    try {
      await api.delete(`/posters/${poster.id}`);
      toast.success("Poster deleted");
      onDeleted();
      onClose();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/85 backdrop-blur-sm p-4">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl bg-surface border border-stroke my-8 grid grid-cols-1 md:grid-cols-2" data-testid="poster-detail-dialog">
        <div className="relative bg-ink">
          <MagnifierImage src={imgs[imgIdx]} alt={poster.title} className="w-full h-full max-h-[70vh]" imgClassName="w-full h-full max-h-[70vh] object-contain" />
          {imgs.length > 1 && (
            <>
              <button onClick={() => setImgIdx((i) => (i - 1 + imgs.length) % imgs.length)}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-ink/80 border border-stroke p-2 text-paper">
                <ChevronLeft size={18} />
              </button>
              <button onClick={() => setImgIdx((i) => (i + 1) % imgs.length)}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-ink/80 border border-stroke p-2 text-paper">
                <ChevronRight size={18} />
              </button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 font-mono text-[10px] bg-ink/80 px-2 py-0.5 text-muted-gray">
                {imgIdx + 1}/{imgs.length}
              </div>
            </>
          )}
        </div>

        <div className="p-6 flex flex-col">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-acid">{poster.band || "Unknown Act"}</div>
              <h2 className="font-display text-4xl text-paper leading-none mt-1">{poster.title}</h2>
            </div>
            <button onClick={onClose} data-testid="poster-detail-close" className="text-muted-gray hover:text-magenta shrink-0">
              <X size={22} />
            </button>
          </div>

          <div className="flex gap-2 mt-3 flex-wrap">
            <span className={`font-mono text-[10px] uppercase px-2 py-0.5 border ${categoryBadge(poster.category)}`}>
              {categoryLabel(poster.category)}
            </span>
            <span className={`font-mono text-[10px] uppercase px-2 py-0.5 border ${variantBadge(poster.variant)}`}>
              {variantLabel(poster.variant)}
            </span>
            {poster.framed && (
              <span className="font-mono text-[10px] uppercase px-2 py-0.5 border border-cyan/40 bg-cyan/10 text-cyan">Framed</span>
            )}
            <span className="font-mono text-[10px] uppercase px-2 py-0.5 border border-stroke text-muted-gray">{poster.condition}</span>
          </div>

          {gain != null && (
            <div className={`mt-4 p-3 border ${gain >= 0 ? "border-acid/40 bg-acid/5" : "border-magenta/40 bg-magenta/5"} flex items-center justify-between`}>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-gray">Value Change</span>
              <span className={`font-mono text-lg font-bold flex items-center gap-1 ${gain >= 0 ? "text-acid" : "text-magenta"}`}>
                {gain >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                {gain >= 0 ? "+" : "-"}{money(Math.abs(gain))}
              </span>
            </div>
          )}

          <div className="mt-4 flex-1">
            <Row label="Poster Artist" value={poster.poster_artist} />
            <Row label="Print #" value={poster.print_number != null && poster.edition_size != null ? `${poster.print_number} / ${poster.edition_size}` : poster.print_number} />
            <Row label="Dimensions" value={poster.width_in && poster.height_in ? `${poster.width_in}" × ${poster.height_in}"` : null} />
            <Row label="Framer" value={poster.framer} />
            <Row label="Show Date" value={poster.show_date} />
            <Row label="Venue" value={venue} />
            <Row label="Purchased" value={poster.purchase_date} />
            <Row label="Purchase Price" value={poster.purchase_price != null ? money(poster.purchase_price) : null} />
            <Row label="Market Value" value={poster.market_value != null ? money(poster.market_value) : null} />
            {poster.notes && <p className="font-mono text-xs text-muted-gray mt-3 leading-relaxed">{poster.notes}</p>}
          </div>

          <div className="mt-5 flex gap-3">
            <button onClick={() => onEdit(poster)} data-testid="poster-edit-button"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 font-mono text-xs uppercase tracking-wider border border-stroke text-paper hover:border-acid hover:text-acid">
              <Pencil size={14} /> Edit
            </button>
            <button onClick={del} data-testid="poster-delete-button"
              className="flex items-center justify-center gap-2 px-4 py-2.5 font-mono text-xs uppercase tracking-wider border border-magenta/50 text-magenta hover:bg-magenta hover:text-white">
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
